import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';

import {
	Button,
	CardLayout,
	FirstNameField,
	FormInput,
	FormToggle,
	LastNameField,
	spacing,
	TelephoneInput,
	UsernameFormInput,
} from '@pletly/ui';
import { Email, Password, PasswordRequirementsHint } from '@pletly/feature-auth';
import { useNewOrganisationMember } from './components/useNewOrganisationMember';
import { RootStackScreenProps } from '../../../navigation/types';
import { withTranslation } from '../../../locales/hoc';

type Props = RootStackScreenProps<'NewOrganisationMember'>;

export const NewOrganisationMember = withTranslation(
	({
		route: {
			params: { organisationUuid, type },
		},
	}: Props) => {
		const { t } = useTranslation();
		const { loading, formMethods, onSubmit } = useNewOrganisationMember({
			organisationUuid,
			type,
		});

		const isFormForCaregiver = type === 'caregiver';

		return (
			<CardLayout
				title={
					isFormForCaregiver
						? t('create-account-for-employee')
						: t('create-account-for-care-recipient')
				}
			>
				<FormProvider {...formMethods}>
					<View style={style.inputsContainer}>
						<UsernameFormInput showGenerator />
						<FormInput
							name="firstName"
							InputComponent={FirstNameField}
							optional={!isFormForCaregiver}
						/>
						<FormInput
							name="lastName"
							InputComponent={LastNameField}
							optional={!isFormForCaregiver}
						/>
						{isFormForCaregiver ? (
							<>
								<FormToggle name="withPassword" label={t('no-email')} />
								{formMethods.watch('withPassword') ? (
									<>
										<FormInput
											name="password"
											InputComponent={Password}
											textContentType="newPassword"
											label={t('initial-password-for-first-login')}
										/>
										<PasswordRequirementsHint value={formMethods.watch('password')} />
									</>
								) : (
									<FormInput name="email" InputComponent={Email} gutter={false} />
								)}
								<FormInput
									name="telephone"
									label={t('telephone')}
									InputComponent={TelephoneInput}
									gutter={false}
									optional
								/>
							</>
						) : (
							<View>
								<FormToggle name="withPassword" label={t('allow-care-recipient-to-use-pletly')} />
								{formMethods.watch('withPassword') && (
									<>
										<FormInput
											name="password"
											InputComponent={Password}
											textContentType="newPassword"
										/>
										<PasswordRequirementsHint value={formMethods.watch('password')} />
									</>
								)}
							</View>
						)}
					</View>
					<Button variant="filled" color="primary" onPress={onSubmit} loading={loading}>
						{t('common.save')}
					</Button>
				</FormProvider>
			</CardLayout>
		);
	},
);

const style = StyleSheet.create({
	inputsContainer: {
		rowGap: spacing(4),
		marginVertical: spacing(4),
	},
});
